import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { selectAudiences } from 'app/pages/Home/slice/selectors';
import { MomentData, MilestonesGridData } from '../types';

/**
 * Custom hook to filter milestones grid data by Customer Audiences
 * Uses substring matching (contains) logic without case conversion
 * Filters on: moment.filters.customerAudiences (array field)
 */
export const useFilterByAudience = (
  milestonesData: MilestonesGridData | null
): MilestonesGridData | null => {
  // Get selected audiences from Redux state
  const audiences = useSelector(selectAudiences);
  const selectedAudiences = audiences?.selectedAudiences || [];

  // Memoize the filtered data to avoid unnecessary recalculations
  const filteredData = useMemo(() => {
    // If no data, return null
    if (!milestonesData || !milestonesData.data) {
      return null;
    }

    // If no audience filters are selected, return original data
    if (selectedAudiences.length === 0) {
      return milestonesData;
    }

    // Filter the data based on selected customer audiences
    const filtered: MomentData[] = milestonesData.data.filter(moment => {
      const customerAudiences = moment.filters?.customerAudiences;

      // If audience filter is active but moment has no customer audiences, exclude it
      if (
        !customerAudiences ||
        !Array.isArray(customerAudiences) ||
        customerAudiences.length === 0
      ) {
        return false;
      }

      // Check if any selected audience is contained in any of the customer audiences
      // Using substring matching (contains logic)
      return selectedAudiences.some(selectedAud =>
        customerAudiences.some(audience => audience.includes(selectedAud))
      );
    });

    return {
      ...milestonesData,
      data: filtered
    };
  }, [milestonesData, selectedAudiences]);

  return filteredData;
};
